import { PublicKey } from "@solana/web3.js";
import { toBaseUnits } from "@oxminter/shared/amounts";

export interface Recipient {
  address: string;
  /** Human-readable amount exactly as written in the file. */
  amount: string;
  /** Integer base units. */
  raw: bigint;
  line: number;
}

export interface ParseResult {
  recipients: Recipient[];
  errors: { line: number; text: string; reason: string }[];
  duplicates: { address: string; lines: number[] }[];
  totalRaw: bigint;
}

const HEADER = /^\s*(address|wallet|recipient|pubkey)\s*[,;\t]/i;

/**
 * Parses a recipient list. Accepts comma, semicolon or tab separation, an
 * optional header row, blank lines and `#` comments.
 *
 * Every failure mode here is one that costs money if it passes silently:
 * a mistyped address burns tokens into a void, a duplicated row double-pays,
 * and an amount with more decimals than the mint would be truncated. All three
 * are reported per-line and block the send rather than being cleaned up.
 */
export function parseRecipients(text: string, decimals: number): ParseResult {
  const recipients: Recipient[] = [];
  const errors: ParseResult["errors"] = [];
  const seen = new Map<string, number[]>();

  const lines = text.split(/\r?\n/);
  lines.forEach((rawLine, i) => {
    const line = i + 1;
    const trimmed = rawLine.trim();
    if (!trimmed || trimmed.startsWith("#")) return;
    if (i === 0 && HEADER.test(rawLine)) return;

    const parts = trimmed.split(/[,;\t]/).map((p) => p.trim());
    if (parts.length < 2) {
      errors.push({ line, text: trimmed, reason: "expected two columns: address, amount" });
      return;
    }
    const [address, amount] = parts;

    let owner: PublicKey;
    try {
      owner = new PublicKey(address);
    } catch {
      errors.push({ line, text: trimmed, reason: "not a valid base58 Solana address" });
      return;
    }
    // A pubkey that is not on the ed25519 curve is a PDA. It cannot sign, and
    // sending to its default ATA usually means the tokens are unrecoverable.
    if (!PublicKey.isOnCurve(owner.toBytes())) {
      errors.push({
        line,
        text: trimmed,
        reason: "off-curve address (a program-derived address, not a wallet)",
      });
      return;
    }

    let raw: bigint;
    try {
      raw = BigInt(toBaseUnits(amount, decimals).toString());
    } catch (e) {
      errors.push({ line, text: trimmed, reason: (e as Error).message });
      return;
    }
    if (raw <= 0n) {
      errors.push({ line, text: trimmed, reason: "amount must be greater than zero" });
      return;
    }

    const key = owner.toBase58();
    seen.set(key, [...(seen.get(key) ?? []), line]);
    recipients.push({ address: key, amount, raw, line });
  });

  const duplicates = [...seen.entries()]
    .filter(([, ls]) => ls.length > 1)
    .map(([address, lines]) => ({ address, lines }));

  const totalRaw = recipients.reduce((a, r) => a + r.raw, 0n);
  return { recipients, errors, duplicates, totalRaw };
}

export const SAMPLE_CSV = `# One recipient per line: <wallet address>,<amount>
# Comma, semicolon or tab separated. A header row is optional.
# Lines starting with # are ignored.
#
# 7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU,1000
# 9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM,250.5
`;
